import { useQuery } from '@tanstack/react-query';

import { mockList } from '@/lib/mock/db';
import { queryKeys } from '@/lib/queryKeys';
import type { Department } from '@/lib/types';

/**
 * 여러 화면이 함께 보는 기준 데이터 — 주차 · 부서 · 현장.
 *
 * v1은 화면마다 `supabase.from('weeks').select(...)` 를 다시 적었고, 정렬 기준도 조금씩 달랐다
 * (어떤 화면은 `sort_order`, 어떤 화면은 이름순). 같은 목록이 화면마다 다른 순서로 보였다는 뜻이다.
 * 여기 모아 두면 정렬과 캐시 키가 한 번에 정해진다.
 *
 * **필터는 하지 않는다.** 비활성 부서를 감출지, 준공 현장을 뺄지는 화면마다 다르다 —
 * 부서 관리 화면은 비활성도 봐야 하고 주간회의는 안 봐야 한다. 그 판단은 호출한 쪽에 남긴다.
 */

/** 최근 주차가 앞. "다음 주차"·"직전 주차"가 이 순서를 전제로 한다. */
export function useWeeks() {
  return useQuery({
    queryKey: queryKeys.weeks,
    queryFn: () => mockList('weeks'),
    select: (weeks) => [...weeks].sort((a, b) => b.startDate.localeCompare(a.startDate)),
  });
}

/** 부서 목록 — <b>부서관리에서 정한 차례</b>({@code sortOrder}). 받은 쪽에서 다시 정렬하지 않는다(아래 ══ 규칙). */
export function useDepartments() {
  return useQuery({
    queryKey: queryKeys.departments,
    queryFn: () => mockList('departments'),
    // 서버는 null 칸을 아예 빼고 보낸다 — 뿌리 부서의 parentId 가 undefined 로 오면 트리가 「없음」과 갈라 읽는다.
    select: (departments) =>
      [...departments]
        .map((department) => ({
          ...department,
          parentId: department.parentId ?? null,
          chartSlot: department.chartSlot ?? null,
          chartDx: department.chartDx ?? 0,
          chartDy: department.chartDy ?? 0,
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder),
  });
}

/*
  ══ 부서·현장을 늘어놓는 차례는 하나다 ══ (사용자 지시 2026-09-14)

  {@link useDepartments} · {@link useSites} 가 돌려준 차례를 <b>그대로 쓴다</b>. 그것이
  부서관리·현장관리에서 사람이 끌어 정한 `sortOrder` 이고, <b>주간업무가 세우는 차례</b>와 같은
  값이다. 인력배치표 명부·전술판도 여기에 묶인다.

  ★ 이 규칙을 어기는 방법은 하나뿐이다: 받은 목록에 `.sort()` 를 한 번 더 거는 것. 실제로
    전술판이 그랬다 — 격자의 빈 자리를 줄이려고 <b>인원이 많은 칸을 앞으로</b> 세웠고, 그 결과
    사람 많은 현장이 본사 부서보다 위에 깔렸다(2026-09-10 → 2026-09-14 되돌림).

  ★ 그 `.sort()` 는 언제나 <b>그럴듯한 이유</b>와 함께 온다(빈 자리·뒤처진 현장·이름순). 걸기
    전에 물어야 하는 것: 이 화면만의 차례가 정말 필요한가, 아니면 주간업무와 <b>같은 줄</b>을
    보고 싶은 사람이 더 많은가.

  ★ 예외는 <b>사람이 화면에서 고른 정렬</b>뿐이다 — 현장 정보의 정렬 상자처럼, 고른 사람이 그
    차례를 알고 있는 경우.
*/

/**
 * 업무 일정(대시보드·주간업무·월간업무)에서 <b>고를 수 있는</b> 부서인가.
 *
 * 빠지는 이유가 둘이라 조건도 둘이다 — 없어진 부서({@code active})와, 조직에는 있지만 업무를 적지
 * 않는 부서({@code showInWork}, 예: 경영진). 화면마다 조건을 다시 적으면 한쪽만 고쳐진다.
 *
 * <b>지난 기록의 부서명을 찾는 곳에는 쓰지 않는다</b>(달력·주차 보기). 감추기 전에 적어 둔 업무가
 * '부서 없음' 으로 보이게 된다.
 */
export const isWorkDepartment = (department: Department) => department.active && department.showInWork;

/** 현장 목록 — <b>현장관리에서 정한 차례</b>({@code sortOrder}). 받은 쪽에서 다시 정렬하지 않는다(위 ══ 규칙). */
export function useSites() {
  return useQuery({
    queryKey: queryKeys.sites,
    queryFn: () => mockList('sites'),
    select: (sites) => [...sites].sort((a, b) => a.sortOrder - b.sortOrder),
  });
}
